import { createContext } from "react";

import type { SessionUser } from "@/lib/firebase";

// Studio auth session (TODO §9.1.2), modelled as a React context rather than a Redux slice:
// it holds a live ID token and imperative sign-in/out functions that don't belong in the
// serializable store. `"loading"` covers the gap before Firebase resolves the persisted
// session on first paint, so the UI doesn't flash signed-out → signed-in on every load.
export type AuthStatus = "loading" | "in" | "out";

export interface AuthContextValue {
  user: SessionUser | null;
  status: AuthStatus;
  // The current Firebase ID token, eagerly refreshed on every identity change so consumers
  // (the Gridd Library, §8.3.1) can push it without waiting. `null` when signed out — and
  // also when signed in but offline, which is why `tokenFresh` exists below.
  idToken: string | null;
  // §14.6.3 Does this session hold a usable bearer right now? Signed in and offline is a real
  // state: Firebase reports the cached user, so `status` stays `"in"`, while token refresh
  // fails and `idToken` goes null. Without this flag consumers conflate the two — today a
  // null token reads as both "signed out" and "signed in, no network", and only one of those
  // should gate anything.
  tokenFresh: boolean;
  // §14.6.1/§14.6.7 When a token was last minted against the network, for the honest account-
  // menu line ("signed in · offline since Tuesday 14:20") and for the 48h window below.
  lastVerifiedAt: number | null;
  // §14.6.5 Inside the window, cached premium content may still be opened; past it, the
  // catalog still browses but premium slides do not. Derived from `lastVerifiedAt` — see
  // `offlineSession.ts`, which owns the clock and the reasoning behind its length.
  withinOfflineGrace: boolean;
  // §9.2.1 The three providers the project enables. Each resolves `true` once signed in and
  // `false` on failure/cancel (errors are toasted inside — §9.2.2 — never thrown), so a
  // caller can close its dialog on success without its own try/catch.
  signInWithGoogle: () => Promise<boolean>;
  signInWithMicrosoft: () => Promise<boolean>;
  signInWithEmail: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  // Opens the shared sign-in dialog (§9.2). The single entry point every gate routes to —
  // the library's premium-signed-out prompt (§8.3.5), the avatar Sign-in button (§9.3), the
  // export gate (§9.4). `onSignedIn` (optional) runs once the user signs in through the
  // dialog, so a gated action can resume without a second click (§9.4.2); if already signed
  // in it runs immediately and no dialog opens.
  promptSignIn: (onSignedIn?: () => void) => void;
  // On-demand token mint (§8.3.3) — `forceRefresh` re-mints an expired token. Always reads
  // the live user, so it never returns a stale token for a signed-in session.
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
